import { Item, Option } from '@/types/common';
import { Testimony, TestimonyItem } from '@/types/events';
import { v4 as uuidv4 } from 'uuid';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { Rule } from 'antd/lib/form';
import type { TFunction } from 'i18next';

export const getMappedOptionsForUsualSelect = (data: Item[]): Option[] => {
  return data.map((item) => ({
    value: item.value,
    label: item.name,
  }));
};

export const getFormattedDateFromFormDateFieldValue = (value: Dayjs) => {
  return value.format('YYYY-MM-DDT00:00:00.000[Z]');
};

export const isDayAfter = (date: Dayjs, comparison: Dayjs): boolean =>
  date.format('YYYY-MM-DD') > comparison.format('YYYY-MM-DD');

export const isDayBefore = (date: Dayjs, comparison: Dayjs): boolean =>
  date.format('YYYY-MM-DD') < comparison.format('YYYY-MM-DD');

export const disabledDateRelativeToToday = (currentDate: Dayjs, isReporting: boolean): boolean =>
  isReporting ? isDayAfter(currentDate, dayjs.utc()) : isDayBefore(currentDate, dayjs.utc());

export const makeDisabledStartDate =
  (isReporting: boolean, getEndDate: () => Dayjs | null) =>
  (currentDate: Dayjs): boolean => {
    const endDate = getEndDate();
    return disabledDateRelativeToToday(currentDate, isReporting) || (!!endDate && isDayAfter(currentDate, endDate));
  };

export const makeDisabledEndDate =
  (isReporting: boolean, getStartDate: () => Dayjs | null, getEarliestEndDate?: () => Dayjs | null) =>
  (currentDate: Dayjs): boolean => {
    const startDate = getStartDate();
    const earliestEndDate = getEarliestEndDate?.();
    return (
      disabledDateRelativeToToday(currentDate, isReporting) ||
      (!!startDate && isDayBefore(currentDate, startDate)) ||
      (!!earliestEndDate && isDayBefore(currentDate, earliestEndDate))
    );
  };

export const integerNumberRule = (t: TFunction): Rule => ({
  validator: (_, value) => {
    if (value === undefined || value === null || value === '') return Promise.resolve();
    return Number.isInteger(Number(value))
      ? Promise.resolve()
      : Promise.reject(t('common.messages.mustBeInteger'));
  },
});

export const healingStartedNumberRules = (t: TFunction, getCompleted: () => number | string | undefined): Rule[] => [
  {
    required: true,
    message: t('createReportPage.sections.participants.healing.form.messages.startedNumberRequired'),
  },
  integerNumberRule(t),
  {
    validator: (_, value) => {
      const completed = getCompleted();
      if (!!value && (Number(value) < 1 || Number(value) < Number(completed))) {
        return Promise.reject(t('createReportPage.sections.participants.healing.form.messages.startedNumberInvalid'));
      }
      return Promise.resolve();
    },
  },
];

export const healingCompletedNumberRules = (t: TFunction, getStarted: () => number | string | undefined): Rule[] => [
  {
    required: true,
    message: t('createReportPage.sections.participants.healing.form.messages.completedNumberRequired'),
  },
  integerNumberRule(t),
  {
    validator: (_, value) => {
      const started = getStarted();
      if (!!value && (Number(value) < 0 || Number(value) > Number(started))) {
        return Promise.reject(t('createReportPage.sections.participants.healing.form.messages.completedNumberInvalid'));
      }
      return Promise.resolve();
    },
  },
];

dayjs.extend(utc);

export const getFormFieldValueFromObjectKeyValue = (key: string, value: unknown, fieldValueMustBeAnArray?: boolean) => {
  let inputValue;

  if (key.includes('date') && typeof value === 'string') inputValue = dayjs.utc(value);
  else if (typeof value === 'string' && fieldValueMustBeAnArray)
    inputValue = value.split(',').map((value) => value.trim());
  else if (value === 0 || value === 1) inputValue = !!value;
  else inputValue = value;

  return inputValue;
};

export const checkIsFormValuesChanged = <T extends Record<string, any>>(
  initialValues: Partial<T>,
  currentValues: T,
) => {
  return !Object.entries(currentValues).every(([key, value]) => {
    if (dayjs.isDayjs(value) && dayjs.isDayjs(initialValues[key])) {
      return value.isSame(initialValues[key], 'day');
    } else if (value === '') {
      return initialValues[key] === undefined || initialValues[key] === '';
    } else if (Array.isArray(value)) {
      if (value.length) {
        return value.length === initialValues[key]?.length && value.every((item) => initialValues[key]?.includes(item));
      } else {
        return initialValues[key] === undefined || (Array.isArray(initialValues[key]) && !initialValues[key]?.length);
      }
    } else {
      return JSON.stringify(value) === JSON.stringify(initialValues[key]);
    }
  });
};

export const getFormSearchableSelectInitialItems = (
  connectedIdKey: string,
  connectedIdKeyValue: string,
  value: string,
  separator: string,
) => {
  let selectedItems: Item[];

  if (connectedIdKey.includes('ids')) {
    selectedItems = connectedIdKeyValue.split(',').map((id, index) => {
      return {
        value: id.trim(),
        name: value.split(separator)[index].trim(),
      };
    });
  } else {
    selectedItems = [
      {
        value: connectedIdKeyValue,
        name: value,
      },
    ];
  }

  return selectedItems;
};

export const mapTestimoniesToItems = (testimonies: Testimony[] | undefined): TestimonyItem[] => {
  if (!testimonies) return [];
  return testimonies.map((t) =>
    t.type === 'file'
      ? { kind: 'file', uid: uuidv4(), filename: t.filename ?? '', url: t.value }
      : { kind: 'link', uid: uuidv4(), url: t.value },
  );
};

export const MAX_TESTIMONIES = 20;
export const MAX_TESTIMONY_FILE_BYTES = 5 * 1024 * 1024 * 1024;

export const isValidTestimonyUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (trimmed.length === 0 || trimmed.length > 2048) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

export const validateTestimonies = (
  items: TestimonyItem[],
): { cleaned: TestimonyItem[]; valid: boolean } => {
  const cleaned = items
    .filter((item) => !(item.kind === 'link' && item.url.trim() === ''))
    .map((item) => (item.kind === 'link' ? { ...item, url: item.url.trim() } : item));

  const linkUrls = cleaned.filter((item) => item.kind === 'link').map((item) => (item as { url: string }).url);
  const filenames = cleaned
    .filter((item) => item.kind === 'file')
    .map((item) => (item as { filename: string }).filename);
  const hasDuplicateLinks = new Set(linkUrls).size !== linkUrls.length;
  const hasDuplicateFiles = new Set(filenames).size !== filenames.length;

  const valid =
    !hasDuplicateLinks &&
    !hasDuplicateFiles &&
    cleaned.every((item) => item.kind === 'file' || isValidTestimonyUrl(item.url));

  return { cleaned, valid };
};

export const appendTestimoniesToFormData = (formData: FormData, items: TestimonyItem[]): void => {
  items.forEach((item, index) => {
    if (item.kind === 'file') {
      formData.append(`testimonies[${index}][filename]`, item.filename);
    } else {
      formData.append(`testimonies[${index}][url]`, item.url);
    }
  });
};

export function buildReportFormData(
  values: Record<string, any>,
  excludedKeys: string[] = [],
): FormData {
  const formData = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (value == null) return;
    if (excludedKeys.some((k) => key === k || key.includes(k))) return;
    if (key === "optional_curriculum_lesson_ids" && Array.isArray(value)) {
      value.forEach((element, index) => {
        formData.append(`optional_curriculum_lesson_ids[${index}]`, element.toString());
      });
    } else {
      formData.append(key, value.toString());
    }
  });
  return formData;
}
